import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Toaster } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { initGtag, initClarity, trackPageView } from '@/lib/analytics';

// Load LandingPage synchronously to prevent initial flash
import LandingPage from '@/pages/LandingPage';

// Auth context and protected route
import { AuthProvider } from '@/contexts/AuthContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import ChunkErrorBoundary from '@/components/ChunkErrorBoundary';

// Lazy load all other page components for route-based code splitting
const BlogArchive = React.lazy(() => import('@/pages/BlogArchive'));
const BlogPost = React.lazy(() => import('@/pages/BlogPostPage'));
const SeriesPage = React.lazy(() => import('@/pages/SeriesPage'));
const OfferingsPage = React.lazy(() => import('@/pages/OfferingsPage'));
const TrainingPage = React.lazy(() => import('@/pages/TrainingPage'));
const PathwaysPage = React.lazy(() => import('@/pages/PathwaysPage'));
const PathwayDetailPage = React.lazy(() => import('@/pages/PathwayDetailPage'));
const PathwayEnrollmentSuccessPage = React.lazy(() => import('@/pages/PathwayEnrollmentSuccessPage'));
const PathwayEnrollmentCancelPage = React.lazy(() => import('@/pages/PathwayEnrollmentCancelPage'));
const CoursePage = React.lazy(() => import('@/pages/CoursePage'));
const AssessmentPage = React.lazy(() => import('@/pages/AssessmentPage'));
const EnrollmentSuccessPage = React.lazy(() => import('@/pages/EnrollmentSuccessPage'));
const EnrollmentCancelPage = React.lazy(() => import('@/pages/EnrollmentCancelPage'));
const AboutPage = React.lazy(() => import('@/pages/AboutPage'));
const AssessmentsPage = React.lazy(() => import('@/pages/AssessmentsPage'));
const PrivacyPolicy = React.lazy(() => import('@/pages/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('@/pages/TermsOfService'));
const CookiePolicy = React.lazy(() => import('@/pages/CookiePolicy'));
const ContactPage = React.lazy(() => import('@/pages/ContactPage'));
const NewsletterConfirmedPage = React.lazy(() => import('@/pages/NewsletterConfirmedPage'));
const NewsletterErrorPage = React.lazy(() => import('@/pages/NewsletterErrorPage'));
const ResourcesPage = React.lazy(() => import('@/pages/ResourcesPage'));
const ResourceLinkExpiredPage = React.lazy(() => import('@/pages/ResourceLinkExpiredPage'));
const ContactCardPage = React.lazy(() => import('@/pages/ContactCardPage'));
const CopilotWavesFoundationPage = React.lazy(() => import('@/pages/CopilotWavesFoundationPage'));
const CopilotWave3AgenticPage = React.lazy(() => import('@/pages/CopilotWave3AgenticPage'));
const CopilotGovernancePlaybookPage = React.lazy(() => import('@/pages/CopilotGovernancePlaybookPage'));
const CopilotComplianceIrapPage = React.lazy(() => import('@/pages/CopilotComplianceIrapPage'));
const CopilotSecurityRoadmapPage = React.lazy(() => import('@/pages/CopilotSecurityRoadmapPage'));
const CopilotWavesAllPage = React.lazy(() => import('@/pages/CopilotWavesAllPage'));
const CopilotReadiness = React.lazy(() => import('@/pages/CopilotReadinessPage'));
// microsoft-365-health-check
const MicrosoftHealthCheck = React.lazy(() => import('@/pages/MicrosoftHealthCheckPage'));
// identity-assessment
const IdentityAssessment = React.lazy(() => import('@/pages/IdentityAssessmentPage'));
// active-directory-assessment
const ActiveDirectoryAssessment = React.lazy(() => import('@/pages/ActiveDirectoryAssessmentPage'));
// essential-eight-maturity
const EssentialEightMaturity = React.lazy(() => import('@/pages/EssentialEightMaturityPage'));
// smb-cyber-security
const SmbCyberSecurityPage = React.lazy(() => import('@/pages/SmbCyberSecurityPage'));
const CopilotEssentialEightAlignment = React.lazy(() => import('@/pages/CopilotEssentialEightAlignmentPage')); // copilot-essential-eight-alignment
// sharepoint-teams-governance
const SharePointTeamsGovernance = React.lazy(() => import('@/pages/SharePointTeamsGovernancePage'));
// copilot-deployment
const CopilotDeployment = React.lazy(() => import('@/pages/CopilotDeploymentPage'));
// copilot-governance-maturity
const CopilotGovernance = React.lazy(() => import('@/pages/CopilotGovernancePage'));

// ScrollToTop component to handle scroll restoration and page view tracking on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView(pathname);
  }, [pathname]);

  return null;
};

// AppLayout: conditionally renders Navbar/Footer outside /vcard
function AppLayout() {
  const { pathname } = useLocation();
  const isVCard = pathname === '/vcard';
  const hideNavFooter = isVCard;

  return (
    <div className={cn('min-h-screen flex flex-col overflow-x-hidden relative', 'bg-white')}>
      {!hideNavFooter && <Navbar />}
      <main className={cn('w-full', hideNavFooter ? 'min-h-screen' : 'flex-grow relative z-0')}>
        <ChunkErrorBoundary resetKey={pathname}>
        <React.Suspense fallback={
          <div className="min-h-screen flex items-center justify-center">
            <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-solid border-[#422680] border-r-transparent opacity-50"></div>
          </div>
        }>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/services" element={<OfferingsPage />} />
            <Route path="/assessments" element={<AssessmentsPage />} />
            <Route path="/assessments/:slug" element={<AssessmentPage />} />
            <Route path="/training" element={<TrainingPage />} />
            <Route path="/training/:slug" element={<CoursePage />} />
            <Route path="/training/:slug/success" element={<EnrollmentSuccessPage />} />
            <Route path="/training/:slug/cancel" element={<EnrollmentCancelPage />} />
            <Route path="/pathways" element={<PathwaysPage />} />
            <Route path="/pathways/:slug" element={<PathwayDetailPage />} />
            <Route path="/pathways/:slug/success" element={<PathwayEnrollmentSuccessPage />} />
            <Route path="/pathways/:slug/cancel" element={<PathwayEnrollmentCancelPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/blog" element={<BlogArchive />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/series/:slug" element={<SeriesPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/resources/link-expired" element={<ResourceLinkExpiredPage />} />
            <Route path="/resources/copilot-waves-foundation" element={<CopilotWavesFoundationPage />} />
            <Route path="/resources/copilot-wave-3-agentic" element={<CopilotWave3AgenticPage />} />
            <Route path="/resources/copilot-governance-playbook" element={<CopilotGovernancePlaybookPage />} />
            <Route path="/resources/copilot-compliance-irap" element={<CopilotComplianceIrapPage />} />
            <Route path="/resources/copilot-security-roadmap" element={<CopilotSecurityRoadmapPage />} />
            <Route path="/resources/copilot-waves-all" element={<CopilotWavesAllPage />} />
            <Route path="/newsletter/confirmed" element={<NewsletterConfirmedPage />} />
            <Route path="/newsletter/error" element={<NewsletterErrorPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/copilot-readiness" element={<CopilotReadiness />} />
            {/* microsoft-365-health-check */}
            <Route path="/microsoft-365-health-check" element={<MicrosoftHealthCheck />} />
            {/* identity-assessment */}
            <Route path="/identity-assessment" element={<IdentityAssessment />} />
            {/* active-directory-assessment */}
            <Route path="/active-directory-assessment" element={<ActiveDirectoryAssessment />} />
            {/* essential-eight-maturity */}
            <Route path="/essential-eight-maturity" element={<EssentialEightMaturity />} />
            {/* smb-cyber-security */}
            <Route path="/smb-cyber-security" element={<SmbCyberSecurityPage />} />
            <Route path="/copilot-essential-eight-alignment" element={<CopilotEssentialEightAlignment />} />{/* copilot-essential-eight-alignment */}
            {/* sharepoint-teams-governance */}
            <Route path="/sharepoint-teams-governance" element={<SharePointTeamsGovernance />} />
            {/* copilot-deployment */}
            <Route path="/copilot-deployment" element={<CopilotDeployment />} />
            {/* copilot-governance-maturity */}
            <Route path="/copilot-governance" element={<CopilotGovernance />} />
            <Route path="/vcard" element={<ContactCardPage />} />
          </Routes>
        </React.Suspense>
        </ChunkErrorBoundary>
      </main>
      {!hideNavFooter && <Footer />}
      <Toaster />
    </div>
  );
}

function App() {
  useEffect(() => {
    initGtag();
    initClarity();
  }, []);

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AuthProvider>
        <ScrollToTop />
        <Helmet>
          <title>Educ4te - Enterprise Cybersecurity Training & Certification</title>
          <meta name="description" content="Transform your team with industry-leading cybersecurity training, certifications, and security assessments. Protect your business with Educ4te's proven solutions." />
          <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
        </Helmet>
        <AppLayout />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
